<script setup>
import { onMounted, ref } from "vue";

import { onboardingHomeSeen, onboardingSetupArtClicked } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";

import BaseButton from "../BaseButton.vue";
import OnboardDialog from "./OnboardDialog.vue";

defineEmits(["success"]);

onMounted(() => trackEvent(onboardingHomeSeen()));

const steps = [
  { title: "Create your ART", detail: "Name your train and set the basics." },
  { title: "Add your teams", detail: "Drop in the teams that plan together." },
  { title: "Run PI Planning", detail: "Open your first session and start planning." },
];

const dialogOpen = ref(false);

function startOnboarding() {
  trackEvent(onboardingSetupArtClicked());
  dialogOpen.value = true;
}
</script>

<template>
  <section class="onboard-home-page">
    <div class="hero">
      <div class="hero-copy">
        <p class="eyebrow">Welcome aboard</p>
        <h1 class="title">Let's set up your first ART</h1>
        <p class="subtitle">
          It only takes a minute — you'll have a working ART and your first PI Planning session
          ready to go.
        </p>
      </div>
      <BaseButton
        class="cta"
        variant="filled"
        color="primary"
        size="medium"
        @click="startOnboarding"
      >
        Set up my ART →
      </BaseButton>
      <p class="reassurance">Just for you — invite your team whenever you're ready.</p>
    </div>

    <div class="how-it-works">
      <p class="how-it-works-label">How it works</p>
      <ol class="steps">
        <li v-for="(step, i) in steps" :key="step.title" class="step">
          <span class="step-number">{{ i + 1 }}</span>
          <div class="step-text">
            <p class="step-title">{{ step.title }}</p>
            <p class="step-detail">{{ step.detail }}</p>
          </div>
        </li>
      </ol>
    </div>

    <OnboardDialog v-model:open="dialogOpen" @success="$emit('success', $event)" />
  </section>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.onboard-home-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 64px;
  min-height: calc(100vh - 40px);
  padding: 56px 24px;
  box-sizing: border-box;
}

.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 560px;
  text-align: center;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.eyebrow {
  @include typography.extra-small("semi-bold");

  color: c.$grey-neutral-50;
  text-transform: uppercase;
  margin: 0;
}

.title {
  @include typography.display;

  color: c.$text-color;
  text-wrap: balance;
  margin: 0;
}

.subtitle {
  @include typography.large;

  color: c.$text-subtle;
  text-wrap: balance;
  margin: 0;
}

.cta {
  margin-top: 32px;
}

.reassurance {
  @include typography.small("regular");

  color: c.$grey-neutral-50;
  margin: 14px 0 0;
}

.how-it-works {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 720px;
}

.how-it-works-label {
  @include typography.extra-small("semi-bold");

  color: c.$grey-neutral-50;
  text-transform: uppercase;
  margin: 0;
}

.steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}

.step {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 32px;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  & + & {
    border-left: 1px solid c.$border-subtle;
  }
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: c.$main;
  color: c.$white;

  @include typography.small("semi-bold");
}

.step-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step-title {
  @include typography.medium("semi-bold");

  color: c.$text-color;
  margin: 0;
}

.step-detail {
  @include typography.small("regular");

  color: c.$text-subtle;
  margin: 0;
}

@media (width <= 767px) {
  .steps {
    grid-template-columns: 1fr;
    max-width: 420px;
    margin: 0 auto;
  }

  .step {
    padding: 16px 0;

    & + & {
      border-left: none;
      border-top: 1px solid c.$border-subtle;
    }
  }
}
</style>
